import { FC, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Tab,
  TabIndicator,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffectOnce } from 'usehooks-ts';

import { InputToggler } from '../../../components';
import {
  useGetFormTemplateByIdQuery,
  useUpdateFormTemplateMutation,
  useCreateFormTemplateMutation,
  displayNotification,
} from '../../../services';
import { useAppDispatch, useTypedSelector } from '../../../hooks';
import { FormFeatures, FormFields } from '../components';
import { resetFormTemplate, updateTemplateName } from '../form-slice';
import { FormFiled } from '../../../models';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const SavedFormTemplate: FC = () => {
  const [isFormTemplateNameInvalid, setIsFormTemplateNameInvalid] =
    useState(false);
  const { formTemplateId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const formTemplate = useTypedSelector(state => state.form);

  const { data } = useGetFormTemplateByIdQuery(Number(formTemplateId), {
    skip: !formTemplateId,
    refetchOnMountOrArgChange: true,
  });

  const [updateFormTemplate, { isLoading: isUpdateFormTemplateLoading }] =
    useUpdateFormTemplateMutation();
  const [createFormTemplate, { isLoading: isCreateFormTemplateLoading }] =
    useCreateFormTemplateMutation();

  useEffectOnce(() => {
    dispatch(resetFormTemplate());
  });

  const navigateToPreviousPage = () => navigate(`/form-builder`);

  async function onSubmit() {
    if (!formTemplate.name) return setIsFormTemplateNameInvalid(true);

    try {
      // Neither trigger is unwrapped: a rejected publish resolved normally and
      // the builder navigated away, discarding the whole template being edited.
      let response: unknown;

      if (formTemplateId) {
        const formData = {
          deletedFields: [] as (string | number)[],
          updatedFields: [] as FormFiled[],
          newFields: [] as FormFiled[],
          features: formTemplate.features,
        };

        data?.data.fields.forEach(baseFormTemplateFiled => {
          const existingFormTemplateFiled = formTemplate.fields.find(
            updatedFormTemplateFiled =>
              baseFormTemplateFiled.id === updatedFormTemplateFiled.id
          );

          if (existingFormTemplateFiled) {
            if (
              JSON.stringify(existingFormTemplateFiled) !==
              JSON.stringify(baseFormTemplateFiled)
            )
              formData.updatedFields.push(existingFormTemplateFiled);
          } else {
            formData.deletedFields.push(baseFormTemplateFiled.id);
          }
        });

        formTemplate.fields.forEach(updatedFormTemplateFiled => {
          const existingFormTemplateFiled = data?.data.fields.find(
            baseFormTemplateFiled =>
              baseFormTemplateFiled.id === updatedFormTemplateFiled.id
          );

          if (!existingFormTemplateFiled) {
            const { id, ...updatedFormTemplateFiledWithoutId } =
              updatedFormTemplateFiled;
            formData.newFields.push(
              updatedFormTemplateFiledWithoutId as FormFiled
            );
          }
        });

        response = await updateFormTemplate({
          id: Number(formTemplateId),
          name: formTemplate.name,
          ...formData,
        });
      } else {
        response = await createFormTemplate({
          name: formTemplate.name,
          features: formTemplate.features,
          fields: formTemplate.fields.map(f => {
            const { id, ...newFormFieldWithoutId } = f;
            return newFormFieldWithoutId as FormFiled;
          }),
        });
      }

      if (!isMutationSuccess(response)) {
        notifyMutationFailure(response, {
          title: 'Could not publish this form',
          description:
            'Your form was not saved — it is still here. Please try again.',
        });
        return;
      }

      navigateToPreviousPage();
    } catch (error) {
      console.error(error);
      displayNotification({
        title: 'Something went wrong!',
        description:
          'Your form was not saved. Please try this action later or contact BrickLynk support.',
        status: 'error',
      });
    }
  }

  return (
    <Box mt="4">
      <HStack justify="space-between" align="start" py="1">
        <Box flexBasis="60%">
          <InputToggler
            placeholder="Enter The Form Name"
            inputValue={formTemplate.name}
            onInputChange={value => {
              dispatch(
                updateTemplateName({
                  name: value,
                })
              );
              setIsFormTemplateNameInvalid(!value);
            }}
            isInvalid={isFormTemplateNameInvalid}
          />
        </Box>

        <HStack justify="end">
          {/* TODO: reuse after implementation */}
          {/* <Button>Preview</Button> */}
          <Button
            type="submit"
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
            onClick={onSubmit}
            isDisabled={isFormTemplateNameInvalid}
            isLoading={
              isCreateFormTemplateLoading || isUpdateFormTemplateLoading
            }
          >
            Publish
          </Button>
        </HStack>
      </HStack>

      <Tabs
        position="relative"
        variant="unstyled"
        colorScheme="orange"
        bg="white"
      >
        <TabList bg="custom_gray.2">
          <Tab
            fontSize="sm"
            fontWeight="semibold"
            color="custom_gray.1"
            _selected={{ color: 'custom_orange.2' }}
          >
            Form Builder
          </Tab>
          <Tab
            fontSize="sm"
            fontWeight="semibold"
            color="custom_gray.1"
            _selected={{ color: 'custom_orange.2' }}
          >
            Settings
          </Tab>
        </TabList>
        <TabIndicator
          mt="-1.5px"
          height="2px"
          bg="custom_orange.2"
          borderRadius="1px"
        />

        <TabPanels
          borderRadius="md"
          boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
          border="1px"
          borderColor="custom_gray.3"
        >
          <TabPanel p="0" minH="80vh" className="test">
            <FormFields fields={formTemplate.fields} />
          </TabPanel>
          <TabPanel>
            <FormFeatures features={formTemplate.features} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};
